import { useLayoutEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMediaQuery } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import { CampaignHeader, CampaignLayout } from '@/layouts/campaign.layout';
import { EditorSheet } from './editor-sheet';
import {
  EDITOR_DESKTOP_MEDIA_QUERY,
  type EditorChrome,
} from './use-editor-chrome';

export interface EditorTab<TId extends string> {
  id: TId;
  label: string;
  content: React.ReactNode;
  hasError?: boolean;
}

export type PublishState = 'clean' | 'dirty' | 'pending' | 'published';

export interface VisualEditorLayoutProps<TId extends string> {
  title: string;
  backTo: string;
  tabs: readonly EditorTab<TId>[];
  chrome: EditorChrome<TId>;
  canvas: React.ReactNode;
  active: {
    checked: boolean;
    onCheckedChange: (value: boolean) => void;
    label: string;
  };
  publish: { state: PublishState; onPublish: () => void };
  /** Where the changes show up once published; defaults to the customers' app. */
  notice?: string;
}

function PublishButton({
  state,
  onPublish,
}: VisualEditorLayoutProps<string>['publish']) {
  const { LL } = useI18nContext();

  if (state === 'dirty') {
    return <Button onClick={onPublish}>{LL.appEditor.publish()}</Button>;
  }
  if (state === 'pending') {
    return (
      <Button disabled>
        <Loader2 aria-hidden className="animate-spin" />
        {LL.appEditor.publishing()}
      </Button>
    );
  }
  return (
    <Button disabled>
      <Check aria-hidden />
      {LL.appEditor.published()}
    </Button>
  );
}

/**
 * Common shell of the visual editors (AD-028): top bar, canvas, "Ativo" row,
 * publish notice and the tabs. From 1024px the tabs sit in an inspector at the
 * right; below, in a bottom sheet under the canvas.
 */
export function VisualEditorLayout<TId extends string>({
  title,
  backTo,
  tabs,
  chrome,
  canvas,
  active,
  publish,
  notice,
}: VisualEditorLayoutProps<TId>) {
  const { LL } = useI18nContext();
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState(0);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    setBodyHeight(body.getBoundingClientRect().height);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setBodyHeight(entry.contentRect.height);
    });
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  const activeRow = (
    <div className="space-y-1 px-4 py-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="editor-active">{active.label}</Label>
        <Switch
          id="editor-active"
          checked={active.checked}
          onCheckedChange={active.onCheckedChange}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {notice ?? LL.appEditor.unpublishedNotice()}
      </p>
    </div>
  );

  const tabList = (
    <TabsList className="mx-4 mb-2 max-md:mx-0">
      {tabs.map((tab) => (
        <TabsTrigger
          key={tab.id}
          value={tab.id}
          aria-invalid={tab.hasError || undefined}
        >
          {tab.label}
          {tab.hasError && (
            <span
              aria-hidden
              className="ml-1.5 h-1.5 w-1.5 rounded-full bg-destructive"
            />
          )}
        </TabsTrigger>
      ))}
    </TabsList>
  );

  // `forceMount` keeps fields such as the rich text editor alive across tabs.
  const panels = tabs.map((tab) => (
    <TabsContent
      key={tab.id}
      value={tab.id}
      forceMount
      hidden={chrome.activeTab !== tab.id}
      className="px-4 pb-4"
    >
      {tab.content}
    </TabsContent>
  ));

  return (
    <CampaignLayout>
      <CampaignHeader
        title={title}
        backTo={backTo}
        backVariant="close"
        actions={<PublishButton {...publish} />}
      />
      <div
        ref={bodyRef}
        className="row-start-2 col-start-1 flex min-h-0 max-lg:flex-col"
      >
        <div className="min-h-0 min-w-0 flex-1">{canvas}</div>
        <Tabs
          value={chrome.activeTab}
          onValueChange={(value) => {
            const tab = tabs.find((candidate) => candidate.id === value);
            if (tab) chrome.setActiveTab(tab.id);
          }}
          className="contents"
        >
          {isDesktop ? (
            <aside className="flex w-[380px] shrink-0 flex-col border-l bg-background">
              {activeRow}
              {tabList}
              <div className="min-h-0 flex-1 overflow-y-auto">{panels}</div>
            </aside>
          ) : (
            <EditorSheet
              snap={chrome.snap}
              onSnapChange={chrome.setSnap}
              bodyHeight={bodyHeight}
              handleLabel={LL.appEditor.sheet.handle()}
              header={
                <>
                  {activeRow}
                  {tabList}
                </>
              }
            >
              {panels}
            </EditorSheet>
          )}
        </Tabs>
      </div>
    </CampaignLayout>
  );
}
